import React, { FunctionComponent } from 'react'
import { useTranslation } from 'react-i18next'

import {
  StyledClinToastBar,
  StyledMessage,
  StyledCloseIcon,
  StyledIcon,
  StyledDismiss
} from './ClinToastBar.styles'
import { ClinTheme } from '../../ClinTheme'
import { ClinIcon } from '../ClinIcon'
import { ClinText } from '../ClinText'
import { ClinIconPathName } from '../ClinIcon/ClinIcon.paths'
import CheckCircleIcon from '../../assets/media/icons/check-circle-green.svg'
import InfoAccentIcon from '../../assets/media/icons/info-accent.svg'

export enum AnnounceMode {
  Success = 'Success',
  Information = 'Information',
  Warning = 'Warning',
  Error = 'Error'
}

interface IAnnounce {
  /** The translation key used for retrieving content from squidex */
  transKey: string
}

/**
 * Look up record/table for returning a toast title value
 * @usage:
 *
 * const { transKey } = announceModeRecord[AnnounceMode.Success]
 *
 */
export const announceModeRecord: Record<AnnounceMode, IAnnounce> = {
  [AnnounceMode.Success]: {
    transKey: 'announce_mode:success'
  },
  [AnnounceMode.Information]: {
    transKey: 'announce_mode:information'
  },
  [AnnounceMode.Warning]: {
    transKey: 'announce_mode:warning'
  },
  [AnnounceMode.Error]: {
    transKey: 'announce_mode:error'
  }
}

export interface IClinAnnounceBarProps {
  /** The title of the toast - defaults to the mode */
  title?: string
  /** The message of the toast */
  message?: string
  /** The mode of the toast */
  mode?: AnnounceMode
  /** Determines if the close x is displayed */
  isDismissible?: boolean
  /** Determines if the dismiss button is displayed */
  isDismissibleText?: boolean
  /** Add bottom margin */
  marginBottom?: number
  /** Add top margin */
  marginTop?: number
  /** Add toast height */
  height?: number
  /** Add icon position */
  iconPosition?: string
  /** The mode of the toast */
  handleClose?: (event: React.MouseEvent<HTMLAnchorElement>) => void
}

export const getIconNameForMode = (mode: AnnounceMode): string => {
  switch (mode) {
    case AnnounceMode.Information:
      return InfoAccentIcon
    case AnnounceMode.Success:
      return CheckCircleIcon
    case AnnounceMode.Error:
      return InfoAccentIcon
    default:
      return InfoAccentIcon
  }
}

export const ClinToastBar: FunctionComponent<IClinAnnounceBarProps> = ({
  children,
  title,
  message,
  mode = AnnounceMode.Error,
  isDismissible,
  isDismissibleText,
  marginTop,
  marginBottom,
  height,
  iconPosition,
  handleClose
}) => {
  const { t } = useTranslation()
  return (
    <StyledClinToastBar
      mode={mode}
      marginTop={marginTop}
      marginBottom={marginBottom}
      height={height}
      iconPosition={iconPosition}
      className="clin-announce"
    >
      <StyledIcon width={24} height={24} src={getIconNameForMode(mode)} />
      <StyledMessage>
        <ClinText
          as="div"
          color={'inherit'}
          fontWeight={ClinTheme.fontWeights.bold}
          lineHeight={ClinTheme.lineHeights.body}
        >
          {title ?? t(`${announceModeRecord[mode].transKey}`)}
        </ClinText>
        <ClinText
          as="div"
          color={'inherit'}
          lineHeight={ClinTheme.lineHeights.body}
        >
          {children || message}
        </ClinText>
      </StyledMessage>
      {isDismissible && (
        <StyledCloseIcon onClick={(event) => handleClose?.(event)}>
          <ClinIcon iconName={ClinIconPathName.Cross} />
        </StyledCloseIcon>
      )}
      {isDismissibleText && (
        <StyledCloseIcon onClick={(event) => handleClose?.(event)}>
          <StyledDismiss as="div">{t('common:buttons.dismiss')}</StyledDismiss>
        </StyledCloseIcon>
      )}
    </StyledClinToastBar>
  )
}
